import {
  Box,
  Button,
  HStack,
  Image,
  Input,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CheckCircle2, Sparkles, UploadCloud } from "lucide-react";
import { EyebrowBadge, FormField, SectionCard } from "../components";
import { ONBOARDING_LINKS, type DocumentKey } from "../constants";
import type { FormData } from "../types";

interface Step4Props {
  data: FormData;
  updateData: (field: string, value: string | boolean) => void;
  fieldErrors: Record<string, string>;
  uploadedDocs: Partial<Record<DocumentKey, string>>;
  logoPreviewUrl?: string;
  handleFileSelect: (key: DocumentKey) => void;
  handleFileChange: (
    key: DocumentKey,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  fileInputRefs: React.MutableRefObject<
    Partial<Record<string, HTMLInputElement | null>>
  >;
}

export function Step4Experience({
  data,
  updateData,
  fieldErrors,
  uploadedDocs,
  logoPreviewUrl,
  handleFileSelect,
  handleFileChange,
  fileInputRefs,
}: Step4Props) {
  return (
    <Box spaceY={8}>
      <Box spaceY={3}>
        <EyebrowBadge stepId={4} />
        <Box spaceY={2}>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color="gray.900"
            letterSpacing="tight"
          >
            Experiencia y beneficios
          </Text>
          <Text fontSize="lg" color="gray.500" maxW="2xl">
            Personaliza el portal, define cómo te contactan tus arrendatarios y
            decide si quieres sumarles una capa extra de valor desde el primer
            día.
          </Text>
        </Box>
      </Box>

      <SectionCard
        title="Identidad visual"
        description="Carga el logo que mostrará tu portal de pagos sobre fondo claro."
      >
        <Box>
          <Box
            onClick={() => handleFileSelect("logo")}
            data-onboarding-error={fieldErrors.logo ? "true" : undefined}
            w="full"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={fieldErrors.logo ? "red.400" : "gray.200"}
            borderRadius="lg"
            bg="gray.50"
            px={6}
            py={12}
            textAlign="center"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ borderColor: "palomma.300", bg: "palomma.50" }}
          >
            {logoPreviewUrl ? (
              <Box
                mb={5}
                h={28}
                w="full"
                maxW="sm"
                overflow="hidden"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
                bg="white"
              >
                <Image
                  src={logoPreviewUrl}
                  alt="Logo cargado"
                  h="full"
                  w="full"
                  objectFit="contain"
                  p={3}
                />
              </Box>
            ) : (
              <Box
                h={16}
                w={16}
                bg="palomma.50"
                color="palomma.500"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={5}
              >
                <UploadCloud size={32} />
              </Box>
            )}
            <Box spaceY={1.5}>
              <Text fontSize="2xl" fontWeight="semibold" color="gray.900">
                {uploadedDocs["logo"]
                  ? uploadedDocs["logo"]
                  : "Sube tu logo en PNG"}
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="md" mx="auto">
                {uploadedDocs["logo"]
                  ? "Archivo cargado correctamente. Si quieres, puedes reemplazarlo antes de terminar el proceso."
                  : "Recomendamos un logo a color sin fondo. El portal de pagos tendrá una base limpia y clara."}
              </Text>
            </Box>
            <Button
              mt={6}
              variant="outline"
              colorPalette="gray"
              onClick={(e) => {
                e.stopPropagation();
                handleFileSelect("logo");
              }}
            >
              {uploadedDocs["logo"] ? "Cambiar archivo" : "Explorar archivos"}
            </Button>
          </Box>

          {fieldErrors.logo && (
            <Text mt={2} fontSize="xs" color="red.500">
              {fieldErrors.logo}
            </Text>
          )}

          <input
            ref={(el) => {
              fileInputRefs.current["logo"] = el;
            }}
            type="file"
            accept=".png"
            hidden
            onChange={(e) => handleFileChange("logo", e)}
          />
        </Box>
      </SectionCard>

      <SectionCard
        title="Canales de atención"
        description="Información visible para tus clientes dentro del portal de pagos."
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gap={6}
        >
          <FormField label="WhatsApp de atención" error={fieldErrors.celular}>
            <Input
              value={data.celular}
              onChange={(e) => updateData("celular", e.target.value)}
              placeholder="300 123 4567"
            />
          </FormField>
          <FormField
            label="Correo de atención"
            error={fieldErrors.correoCliente}
          >
            <Input
              type="email"
              value={data.correoCliente}
              onChange={(e) => updateData("correoCliente", e.target.value)}
              placeholder="soporte@tudominio.com"
            />
          </FormField>
        </Box>
      </SectionCard>

      <Box
        position="relative"
        overflow="hidden"
        borderRadius="lg"
        bg="linear-gradient(135deg, rgba(64, 18, 171, 0.08), rgba(255, 255, 255, 0.8))"
      >
        {data.palommaPlus && (
          <Box
            position="absolute"
            top={0}
            right={0}
            w={48}
            h={48}
            bg="palomma.500"
            opacity={0.15}
            borderRadius="full"
            filter="blur(48px)"
            pointerEvents="none"
          />
        )}
        <Box position="relative" p={8}>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", md: "minmax(0, 1fr) auto" }}
            gap={6}
            alignItems="flex-start"
          >
            <Box spaceY={6}>
              <Box spaceY={3}>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  borderRadius="full"
                  bg="white/80"
                  px={3}
                  py={1}
                >
                  <Sparkles
                    size={14}
                    color="var(--chakra-colors-palomma-600)"
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="palomma.700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    Palomma Plus
                  </Text>
                </Box>
                <Box spaceY={2}>
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color="gray.900"
                    letterSpacing="tight"
                  >
                    Convierte el pago del arriendo en una mejor experiencia
                  </Text>
                  <Text fontSize="sm" color="gray.600" maxW="3xl">
                    Activa nuestro programa de beneficios para tus inquilinos
                    cuando pagan su arriendo. Es un upsell voluntario de $9.900
                    al mes, sin carga operativa para tu equipo.
                  </Text>
                </Box>
              </Box>
              <HStack gap={4} flexWrap="wrap">
                <Link
                  href={ONBOARDING_LINKS.palommaPlus}
                  target="_blank"
                  rel="noreferrer"
                  color="palomma.700"
                  fontWeight="semibold"
                  textDecoration="underline"
                  textUnderlineOffset="4px"
                  fontSize="sm"
                >
                  Conocer Palomma Plus
                </Link>
                <Link
                  href={ONBOARDING_LINKS.palommaPlusBenefits}
                  target="_blank"
                  rel="noreferrer"
                  color="palomma.700"
                  fontWeight="semibold"
                  textDecoration="underline"
                  textUnderlineOffset="4px"
                  fontSize="sm"
                >
                  Ver beneficios
                </Link>
              </HStack>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent={{ md: "flex-end" }}
            >
              <Box
                onClick={() => updateData("palommaPlus", !data.palommaPlus)}
                display="inline-flex"
                alignItems="center"
                gap={3}
                borderRadius="full"
                borderWidth="1px"
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="medium"
                cursor="pointer"
                transition="all 0.2s"
                bg={data.palommaPlus ? "palomma.500" : "white"}
                color={data.palommaPlus ? "white" : "gray.900"}
                borderColor={data.palommaPlus ? "palomma.500" : "gray.200"}
              >
                <Box
                  display="flex"
                  h={6}
                  w={6}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  borderWidth="1px"
                  transition="all 0.2s"
                  borderColor={data.palommaPlus ? "white/40" : "gray.200"}
                  bg={data.palommaPlus ? "white/15" : "gray.100"}
                >
                  {data.palommaPlus && <CheckCircle2 size={16} />}
                </Box>
                {data.palommaPlus
                  ? "Palomma Plus activado"
                  : "Activar Palomma Plus"}
              </Box>
            </Box>

            <Box
              gridColumn={{ md: "1 / -1" }}
              display="grid"
              gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={4}
            >
              <Box borderRadius="lg" bg="white/70" p={5}>
                <Text
                  fontWeight="semibold"
                  color="gray.900"
                  mb={3}
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Tus inquilinos ganan
                </Text>
                <VStack align="start" gap={2} fontSize="sm" color="gray.600">
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-palomma-500)"
                      />
                    </Box>
                    <Text>250 Puntos Colombia por pago</Text>
                  </HStack>
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-palomma-500)"
                      />
                    </Box>
                    <Text>Descuentos en más de 800 marcas</Text>
                  </HStack>
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-palomma-500)"
                      />
                    </Box>
                    <Text>Sorteos mensuales relacionados con arriendo</Text>
                  </HStack>
                </VStack>
              </Box>
              <Box borderRadius="lg" bg="white/70" p={5}>
                <Text
                  fontWeight="semibold"
                  color="gray.900"
                  mb={3}
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Tu comercio gana
                </Text>
                <VStack align="start" gap={2} fontSize="sm" color="gray.600">
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-green-500)"
                      />
                    </Box>
                    <Text>
                      Suscripciones gratis para propietarios y empleados
                    </Text>
                  </HStack>
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-green-500)"
                      />
                    </Box>
                    <Text>Cero carga operativa adicional</Text>
                  </HStack>
                  <HStack gap={2} alignItems="flex-start">
                    <Box flexShrink={0} mt={1}>
                      <CheckCircle2
                        size={16}
                        color="var(--chakra-colors-green-500)"
                      />
                    </Box>
                    <Text>Mayor fidelización y recordación de marca</Text>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
